import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  requestDeleteCloudMaterial,
  requestDeleteTemporary,
  requestSaveTemporary,
  moveCloudMaterialToPersonal,
  updateCloudMaterial,
  updateTemporaryMaterial,
  addMaterialProductLinks,
  removeMaterialProductLinks,
  fetchMaterialProductLinks,
  type ProductLinkInput,
} from '../../../features/materials/api';
import { useToast } from '../../../hooks/useToast';
import { brandConfig } from '../../../config/brands';
import type { Tag } from '../../../features/tags/types';
import type { AnyMaterial, MaterialsTab, TabType } from './types';

export type BulkEditMode = 'none' | 'delete' | 'tags' | 'move' | 'product-link';

interface UseBulkMaterialActionsParams {
  bulkEditMode: BulkEditMode;
  onBulkEditComplete?: () => void;
  /** 現在のタブに表示中の素材リスト */
  materials: AnyMaterial[];
  /** 'storyboard' を除いた現在の素材タブ */
  materialsTab: MaterialsTab;
  activeTab: TabType;
  addPlaceholderForMaterial: (tab: MaterialsTab, material: AnyMaterial) => void;
  removePlaceholderForMaterial: (tab: MaterialsTab, materialId: string) => void;
}

/**
 * SimpleMaterialsList の一括操作 (削除 / フォルダ移動 / タグ追加・削除 /
 * 商品関連付け) と、その選択状態・タグドロップダウン・商品選択モーダルの
 * 状態を集約したフック。ゴミ箱移動の mutation は単体削除 (カードの削除
 * ボタン) からも共用される。
 */
export function useBulkMaterialActions({
  bulkEditMode,
  onBulkEditComplete,
  materials,
  materialsTab,
  activeTab,
  addPlaceholderForMaterial,
  removePlaceholderForMaterial,
}: UseBulkMaterialActionsParams) {
  const queryClient = useQueryClient();
  const toast = useToast();

  // 一括編集モード用の選択状態
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [isTagDropdownOpen, setTagDropdownOpen] = useState<'add' | 'remove' | null>(null);
  // 商品の関連付け（一括）モーダルの開閉。null=閉じる / 'add'=追加 / 'remove'=解除
  const [productSelectorMode, setProductSelectorMode] = useState<'add' | 'remove' | null>(null);
  // 解除モード時、選択中素材に既に紐づいている商品の和集合（重複排除済み）
  const [removableLinks, setRemovableLinks] = useState<ProductLinkInput[]>([]);
  const [tagDropdownPosition, setTagDropdownPosition] = useState({ top: 0, left: 0 });
  const [isCreateTagModalOpen, setCreateTagModalOpen] = useState(false);
  const tagButtonRef = useRef<HTMLButtonElement>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);

  // ゴミ箱移動のmutation
  const deleteCloudMutation = useMutation({
    mutationFn: requestDeleteCloudMaterial,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['cloud-materials'] });
      toast.success('素材をゴミ箱に移動しました');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || '削除に失敗しました';
      toast.error(`削除エラー: ${message}`);
      console.error('Delete error:', error);
    },
  });

  const deleteTemporaryMutation = useMutation({
    mutationFn: requestDeleteTemporary,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['temporary-materials'] });
      toast.success('素材をゴミ箱に移動しました');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || '削除に失敗しました';
      toast.error(`削除エラー: ${message}`);
      console.error('Delete error:', error);
    },
  });

  // 一括編集モードが終了したら選択状態をクリア
  useEffect(() => {
    if (bulkEditMode === 'none') {
      setBulkSelectedIds(new Set());
    }
  }, [bulkEditMode]);

  // タグドロップダウン外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target as Node)) {
        setTagDropdownOpen(null);
      }
    };

    if (isTagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isTagDropdownOpen]);

  // 一括削除処理
  const handleBulkDelete = async () => {
    if (bulkSelectedIds.size === 0) return;

    const count = bulkSelectedIds.size;
    const selectedMaterials = materials.filter((mat) => bulkSelectedIds.has(mat.id));
    if (!selectedMaterials.length) {
      return;
    }

    selectedMaterials.forEach((material) => {
      addPlaceholderForMaterial(materialsTab, material);
    });

    toast.show(`${count}件の素材を削除中...`);

    try {
      await Promise.all(
        selectedMaterials.map((material) => {
          if ('temp_path' in material) {
            return deleteTemporaryMutation.mutateAsync(material.id);
          }
          return deleteCloudMutation.mutateAsync(material.id);
        })
      );

      // データを再取得
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['cloud-materials'] }),
        queryClient.refetchQueries({ queryKey: ['temporary-materials'] }),
      ]);

      // 選択状態をクリア
      setBulkSelectedIds(new Set());

      // 成功トーストを表示
      toast.success(`${count}件の素材をゴミ箱に移動しました`);

      // 一括編集モードを終了
      onBulkEditComplete?.();
    } catch (error) {
      selectedMaterials.forEach((material) => {
        removePlaceholderForMaterial(materialsTab, material.id);
      });
      console.error('一括削除に失敗しました:', error);
      toast.error('一括削除に失敗しました');
    }
  };

  // 一括フォルダ移動処理
  const handleBulkMove = async () => {
    if (bulkSelectedIds.size === 0) return;

    const count = bulkSelectedIds.size;
    const selectedMaterials = materials.filter((mat) => bulkSelectedIds.has(mat.id));
    if (!selectedMaterials.length) {
      return;
    }

    selectedMaterials.forEach((material) => {
      addPlaceholderForMaterial(materialsTab, material);
    });

    const isToCloud = activeTab === 'temporary';
    toast.show(`${count}件の素材を${isToCloud ? '共有' : '個人'}フォルダに移動中...`);

    // 移動はファイルコピー+サムネイル生成等の重い処理のため逐次実行
    let successCount = 0;
    let failCount = 0;
    for (const material of selectedMaterials) {
      try {
        if (isToCloud) {
          await requestSaveTemporary(material.id);
        } else {
          await moveCloudMaterialToPersonal(material.id);
        }
        successCount++;
      } catch (error) {
        failCount++;
        removePlaceholderForMaterial(materialsTab, material.id);
        console.error(`素材 ${material.id} の移動に失敗:`, error);
      }
    }

    // データを再取得
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['cloud-materials'] }),
      queryClient.refetchQueries({ queryKey: ['temporary-materials'] }),
    ]);

    // 選択状態をクリア
    setBulkSelectedIds(new Set());

    if (failCount === 0) {
      toast.success(`${count}件の素材を${isToCloud ? '共有' : '個人'}フォルダに移動しました`);
    } else if (successCount > 0) {
      toast.show(`${successCount}件移動、${failCount}件失敗しました`);
    } else {
      toast.error('一括移動に失敗しました');
    }

    // 一括編集モードを終了
    onBulkEditComplete?.();
  };

  // 一括タグ追加処理
  const handleBulkAddTag = async (tag: Tag) => {
    if (bulkSelectedIds.size === 0) return;

    const selectedMaterials = materials.filter((mat) => bulkSelectedIds.has(mat.id));
    toast.show(`${bulkSelectedIds.size}件の素材にタグを追加中...`);

    try {
      await Promise.all(
        selectedMaterials.map(async (material) => {
          const currentTagIds = material.tags?.map((t: any) => t.id) || [];
          if (!currentTagIds.includes(tag.id)) {
            const newTagIds = [...currentTagIds, tag.id];

            if ('temp_path' in material) {
              // 一時素材（個人フォルダ）
              await updateTemporaryMaterial(material.id, { tags: newTagIds });
            } else {
              // クラウド素材（共有フォルダ）
              await updateCloudMaterial(material.id, { tags: newTagIds });
            }
          }
        })
      );

      // データを再取得
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['cloud-materials'] }),
        queryClient.refetchQueries({ queryKey: ['temporary-materials'] }),
      ]);

      setTagDropdownOpen(null);
      toast.success(`タグ「${tag.name}」を追加しました`);
    } catch (error) {
      console.error('タグ追加に失敗しました:', error);
      toast.error('タグ追加に失敗しました');
    }
  };

  // 一括タグ削除処理
  const handleBulkRemoveTag = async (tag: Tag) => {
    if (bulkSelectedIds.size === 0) return;

    const selectedMaterials = materials.filter((mat) => bulkSelectedIds.has(mat.id));
    toast.show(`${bulkSelectedIds.size}件の素材からタグを削除中...`);

    try {
      await Promise.all(
        selectedMaterials.map(async (material) => {
          const currentTagIds = material.tags?.map((t: any) => t.id) || [];
          const newTagIds = currentTagIds.filter((id: string) => id !== tag.id);

          if ('temp_path' in material) {
            // 一時素材（個人フォルダ）
            await updateTemporaryMaterial(material.id, { tags: newTagIds });
          } else {
            // クラウド素材（共有フォルダ）
            await updateCloudMaterial(material.id, { tags: newTagIds });
          }
        })
      );

      // データを再取得
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['cloud-materials'] }),
        queryClient.refetchQueries({ queryKey: ['temporary-materials'] }),
      ]);

      setTagDropdownOpen(null);
      toast.success(`タグ「${tag.name}」を削除しました`);
    } catch (error) {
      console.error('タグ削除に失敗しました:', error);
      toast.error('タグ削除に失敗しました');
    }
  };

  // 一括商品関連付け処理（追加 / 解除）
  const handleBulkProductLinks = async (
    links: ProductLinkInput[],
    action: 'add' | 'remove',
  ) => {
    if (bulkSelectedIds.size === 0 || links.length === 0) {
      setProductSelectorMode(null);
      return;
    }

    const selectedMaterials = materials.filter((mat) => bulkSelectedIds.has(mat.id));
    if (selectedMaterials.length === 0) {
      setProductSelectorMode(null);
      return;
    }

    const verb = action === 'add' ? '関連付け中' : '関連付けを解除中';
    toast.show(`${selectedMaterials.length}件の${brandConfig.labels.material}を${verb}...`);

    try {
      await Promise.all(
        selectedMaterials.map(async (material) => {
          const folderType: 'temporary' | 'cloud' = 'temp_path' in material ? 'temporary' : 'cloud';
          if (action === 'add') {
            await addMaterialProductLinks(folderType, material.id, links);
          } else {
            await removeMaterialProductLinks(folderType, material.id, links);
          }
        }),
      );

      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['cloud-materials'] }),
        queryClient.refetchQueries({ queryKey: ['temporary-materials'] }),
      ]);

      const doneVerb = action === 'add' ? '関連付けました' : '関連付けを解除しました';
      toast.success(`${selectedMaterials.length}件の${brandConfig.labels.material}に${links.length}件の${brandConfig.labels.productShort}を${doneVerb}`);
      setProductSelectorMode(null);

      // 削除・移動と同じく、完了したら選択を解除して一括編集モードを抜ける
      setBulkSelectedIds(new Set());
      onBulkEditComplete?.();
    } catch (error) {
      console.error('商品の一括関連付けに失敗しました:', error);
      toast.error('商品の一括関連付けに失敗しました');
    }
  };

  // 解除モーダルを開く。選択中素材の既存リンクをプリフェッチして和集合を表示対象に絞る
  const openRemoveProductLinkModal = async () => {
    if (bulkSelectedIds.size === 0) return;
    const selectedMaterials = materials.filter((mat) => bulkSelectedIds.has(mat.id));
    if (selectedMaterials.length === 0) return;

    try {
      const linksLists = await Promise.all(
        selectedMaterials.map((material) => {
          const folderType: 'temporary' | 'cloud' = 'temp_path' in material ? 'temporary' : 'cloud';
          return fetchMaterialProductLinks(folderType, material.id);
        }),
      );

      const dedup = new Map<string, ProductLinkInput>();
      for (const links of linksLists) {
        for (const link of links) {
          const key = `${link.product_type}:${link.product_id}`;
          if (!dedup.has(key)) {
            dedup.set(key, { product_type: link.product_type, product_id: link.product_id });
          }
        }
      }

      const unionLinks = Array.from(dedup.values());
      if (unionLinks.length === 0) {
        toast.show(`関連付けられた${brandConfig.labels.productShort}がありません`);
        return;
      }
      setRemovableLinks(unionLinks);
      setProductSelectorMode('remove');
    } catch (error) {
      console.error('関連付け一覧の取得に失敗しました:', error);
      toast.error('関連付け一覧の取得に失敗しました');
    }
  };

  return {
    bulkSelectedIds, setBulkSelectedIds,
    isTagDropdownOpen, setTagDropdownOpen,
    productSelectorMode, setProductSelectorMode,
    removableLinks, setRemovableLinks,
    tagDropdownPosition, setTagDropdownPosition,
    isCreateTagModalOpen, setCreateTagModalOpen,
    tagButtonRef, tagDropdownRef,
    deleteCloudMutation, deleteTemporaryMutation,
    handleBulkDelete,
    handleBulkMove,
    handleBulkAddTag,
    handleBulkRemoveTag,
    handleBulkProductLinks,
    openRemoveProductLinkModal,
  };
}

export type BulkMaterialActionsApi = ReturnType<typeof useBulkMaterialActions>;
